import Decimal from "decimal.js";
import {
  getMinorUnits,
  type CurrencyCode,
  type ExchangeRateSnapshot,
} from "../schemas/currency";

/**
 * Converts a base-currency amount to a target currency and rounds to that
 * currency's minor unit using ROUND_HALF_UP. Used per line item.
 * @param baseAmount - decimal string in base currency
 * @param rate - decimal string, base→target rate (6dp)
 * @param targetCurrency - ISO code controlling rounding precision
 * @returns decimal string rounded to the target currency's minor unit
 */
export const convertAndRound = (
  baseAmount: string,
  rate: string,
  targetCurrency: CurrencyCode,
): string => {
  const minorUnits = getMinorUnits(targetCurrency);
  return new Decimal(baseAmount)
    .times(new Decimal(rate))
    .toFixed(minorUnits, Decimal.ROUND_HALF_UP);
};

/**
 * Looks up the from→to rate from a snapshot's full pairwise table. The rate is
 * stored explicitly per pair (not derived from a base), so asymmetric and
 * non-triangular rates are honored exactly as uploaded.
 * @param snapshot - exchange-rate snapshot holding the pairwise rate table
 * @param from - source ISO-4217 currency code
 * @param to - target ISO-4217 currency code
 * @returns the from→to rate as a decimal string ("1" when from === to)
 * @throws if the from→to pair is missing from the snapshot's rates
 */
export const crossRate = (
  snapshot: ExchangeRateSnapshot,
  from: CurrencyCode,
  to: CurrencyCode,
): string => {
  if (from === to) {
    return "1";
  }
  const rate = snapshot.rates[from]?.[to];
  if (rate === undefined) {
    throw new Error(`Missing rate in snapshot for ${from}->${to}`);
  }
  return rate;
};
